// ---------------------------------------------------------------------------
// Sessão de Foco no nível do APP — o dono único de "existe uma sessão para
// mostrar" (BUG-164).
//
// ## Por que existe
//
// O `FocusModeModal` era montado por PÁGINA, atrás de um estado local do host:
// `focusSession` na Home, `focusTarget` na BookDetail. Navegar desmonta a
// página, o modal vai junto, e a sessão — que segue viva no servidor — perde
// toda superfície. E voltar não resolvia: nenhum dos dois hosts recuperava
// sessão ativa, então iniciar o Foco na Home, minimizar, ir na Busca e voltar
// devolvia a Home limpa. A única porta que restava no app inteiro era o
// "Retornar ao Foco" da BookDetail daquele livro exato.
//
// O dano não era só de visibilidade: o agendamento da conclusão do pomodoro
// vive num efeito do `useFocusTimer` (`setTimeout` contra `computeDueMs` +
// reconciliação por `visibilitychange`). Desmontar cancelava o timeout, então
// sair da Home no meio de um pomodoro fazia a sessão não concluir sozinha.
//
// ## A divisão
//
//  · `FocusSessionProvider` — montado no `AppRoutes`, ACIMA das rotas. Guarda o
//    pedido, o minimizado e os sinais. Não renderiza o modal.
//  · `FocusSessionHost`     — montado no `AppFrame`, ao lado dos watchers de
//    celebração. Renderiza. Fica ali pelo mesmo motivo que eles: o
//    `FocusModeModal` desenha com `fixed` cru SEM portal, então montado fora do
//    frame escaparia dele no desktop.
//
// ## O que este arquivo NÃO faz
//
// Ele não instancia `useFocusTimer`. A recuperação aqui é uma leitura avulsa
// (`getActiveFocusSession`), e é deliberado: dois timers vivos significariam
// dois agendamentos de conclusão e duas chamadas a `completeFocusSession`
// (idempotente por `focus_{sessionId}`, mas o caminho não deve existir). O
// invariante da skill segue de pé — existe UM `useFocusTimer` no app, dentro do
// modal.
// ---------------------------------------------------------------------------

import { createContext, useContext } from "react";
import type { FocusTarget } from "../hooks/useFocusTimer";
import type { UserBook } from "../../../services/firebase/booksService";
import type { FocusSession } from "../../../services/firebase/focusService";
import { findUserBookByAnyId } from "../../../lib/seo/userBookUrl";

/**
 * O que um host precisa dizer para abrir o Foco.
 *
 * `book` é separado dos campos de exibição de propósito: a BookDetail deixa
 * focar livro que NÃO está na estante (`userBook` nulo, título e capa vindos do
 * catálogo), e o compositor de nota do wrap-up precisa do exemplar quando ele
 * existe. Colapsar os dois num `UserBook` obrigaria a inventar um exemplar
 * falso justamente no caso em que ele não existe.
 */
export interface FocusStartRequest {
  /** Exemplar na estante, quando existe. Habilita "Anotar algo da sessão". */
  book: UserBook | null;
  /** Id de CATÁLOGO — é o que a sessão grava e o que o timer usa. */
  bookId: string;
  title: string;
  coverUrl?: string | null;
  /** Progresso NA UNIDADE DO FORMATO (página em físico, 0-100 em Kindle). */
  currentPages?: number;
  totalPages?: number | null;
  format?: "physical" | "kindle";
}

export interface FocusSessionRequest extends FocusStartRequest {
  /**
   * Alvo escolhido no `FocusSheet`. `null` = sessão recuperada: o
   * `useFocusTimer` a recupera sozinho e o auto-start não deve disparar.
   */
  target: FocusTarget | null;
  /** Recuperada abre no drawer; iniciada agora abre em tela cheia. */
  minimized: boolean;
  /**
   * Sessão RECUPERADA do servidor (BUG-215). Enquanto `book` for `null`, o
   * provider continua tentando resolver o exemplar — quando a estante chega,
   * e por busca direta se a janela dela não o tiver. Sessão iniciada nesta
   * carga do app nunca carrega a flag: o exemplar dela já veio do host.
   */
  recovered?: boolean;
  /**
   * O doc da sessão COMO O PROVIDER O LEU (BUG-228). É o que o `useFocusTimer`
   * adota em vez de ir à rede de novo: a segunda leitura era a que morria no
   * transporte reconectando depois do bloqueio de tela — 8 s de spinner, o
   * erro "Tentar novamente", e um retry que nunca rearmava a recuperação.
   * Sessão iniciada nesta carga do app não carrega o campo.
   */
  session?: FocusSession | null;
}

/** Um exemplar da estante já responde a tudo que o Foco pergunta. */
export function focusRequestFromUserBook(book: UserBook): FocusStartRequest {
  return {
    book,
    bookId: book.bookId,
    title: book.title,
    coverUrl: book.coverUrl,
    currentPages: book.progress || 0,
    totalPages: book.totalPages,
    format: book.format === "kindle" ? "kindle" : "physical",
  };
}

/** Nome do drawer quando NADA identifica o livro — nem doc, nem estante. */
export const RECOVERED_SESSION_FALLBACK_TITLE = "Sessão de leitura";

/**
 * O que o doc da sessão sabe do livro, gravado no `startFocusSession` (BUG-215).
 *
 * É a foto do INSTANTE do start — título, capa e formato —, e existe porque a
 * recuperação não pode depender da estante estar carregada para dizer qual
 * livro está em foco: no cold start a estante chega DEPOIS do claim, e antes
 * desta foto o drawer nascia como "Sessão de leitura" sem capa.
 */
export interface RecoveredSessionBookInfo {
  bookId: string;
  bookTitle?: string | null;
  bookCoverUrl?: string | null;
  bookFormat?: "physical" | "kindle" | null;
}

/**
 * O pedido de uma sessão recuperada, na melhor forma que o dado permite AGORA.
 *
 * Ordem: o exemplar da estante (`shelf`, resolução flexível — sessão antiga pode
 * estar em `apple-X` contra `apple:X`), depois o exemplar buscado DIRETO
 * (`fetched`, quando a janela da estante não o tinha), e só então a foto do
 * doc. O exemplar vence a foto porque é ele que carrega progresso e total —
 * o que o wrap-up precisa para aplicar as páginas e mostrar "N / M".
 *
 * Puro e chamado a cada mudança de estante: é isto que faz a recuperação
 * deixar de ser uma leitura congelada do instante do boot.
 */
export function recoveredFocusRequest(
  session: RecoveredSessionBookInfo,
  shelf: UserBook[],
  fetched: UserBook | null,
): FocusStartRequest {
  const book = findUserBookByAnyId(shelf, session.bookId) ?? fetched;
  if (book) return focusRequestFromUserBook(book);
  const title = session.bookTitle?.trim();
  const coverUrl = session.bookCoverUrl?.trim();
  return {
    book: null,
    bookId: session.bookId,
    title: title || RECOVERED_SESSION_FALLBACK_TITLE,
    coverUrl: coverUrl || null,
    ...(session.bookFormat === "kindle" || session.bookFormat === "physical"
      ? { format: session.bookFormat }
      : {}),
  };
}

export interface FocusSessionContextValue {
  request: FocusSessionRequest | null;
  /** Há sessão em cena (expandida ou no drawer). */
  isActive: boolean;
  minimized: boolean;
  expandSignal: number;
  /**
   * Incrementa quando uma sessão se fecha. Os hosts observam para refazer as
   * leituras que faziam no `onClose` — callback capturado de página desmontada
   * seria stale por construção.
   */
  endedSignal: number;
  startFocus: (input: FocusStartRequest, target: FocusTarget) => void;
  expandFocus: () => void;
}

export const FocusSessionContext = createContext<FocusSessionContextValue | null>(null);

/**
 * Ação pedida por um controle da Live Activity (`livra://foco/...`).
 *
 * `seq` existe porque a MESMA ação pode ser pedida duas vezes seguidas —
 * pausar, retomar, pausar de novo — e o consumidor precisa distinguir "pediu
 * outra vez" de "é o mesmo pedido que eu já apliquei". Sem ele, dois toques
 * idênticos em sequência produziriam um efeito só.
 */
export interface FocusActionRequest {
  action: "pause" | "resume" | "end";
  /** `null` = link sem sessão declarada; o consumidor decide se aceita. */
  sessionId: string | null;
  seq: number;
}

/** Canal privado provider→host: nada disso é assunto das páginas. */
export interface FocusSessionHostValue {
  request: FocusSessionRequest | null;
  expandSignal: number;
  /**
   * Último pedido vindo do card. O provider CAPTURA (é ele que existe no nível
   * do app, então é o único que está montado quando a URL chega) e o host
   * EXECUTA, porque quem tem o `useFocusTimer` é o modal. O provider não
   * poderia executar sem instanciar um segundo timer — dois agendamentos de
   * conclusão, que é o caminho que o `focusSession.ts` inteiro existe para
   * evitar.
   */
  actionRequest: FocusActionRequest | null;
  setMinimized: (v: boolean) => void;
  closeFocus: (outcome?: FocusCloseOutcome) => void;
  /**
   * Livro que o Foco acabou de CONCLUIR (BUG-304) — o host abre a tela de
   * nota e compartilhar sobre a rota em que a sessão fechou. Mora aqui, e não
   * nas páginas, pelo mesmo motivo do modal: a sessão pode fechar em qualquer
   * rota, e a Home/BookDetail só montavam a conclusão dos caminhos manuais.
   */
  finishedBook: UserBook | null;
  dismissFinishedBook: () => void;
}

/** O que o modal diz ao fechar. Ausente = fechamento comum. */
export interface FocusCloseOutcome {
  /** O servidor marcou o livro como lido NESTA sessão (`markedAsRead`). */
  bookFinished: boolean;
}

/**
 * O exemplar a celebrar quando o Foco fecha com o livro concluído (BUG-304).
 *
 * A estante vence a foto do pedido: o pedido foi resolvido no início da sessão
 * e a estante já traz o doc que o servidor acabou de fechar. Sem exemplar em
 * nenhum dos dois não há o que abrir — a tela de conclusão grava nota e
 * compartilhamento NO exemplar (`userBookId`).
 */
export function resolveFinishedBook(
  outcome: FocusCloseOutcome | undefined,
  request: Pick<FocusStartRequest, "book" | "bookId"> | null,
  shelf: UserBook[],
): UserBook | null {
  if (outcome?.bookFinished !== true || !request) return null;
  return findUserBookByAnyId(shelf, request.bookId) ?? request.book ?? null;
}
export const FocusSessionHostContext = createContext<FocusSessionHostValue | null>(null);

export function useFocusSession(): FocusSessionContextValue {
  const ctx = useContext(FocusSessionContext);
  if (!ctx) throw new Error("useFocusSession precisa do FocusSessionProvider");
  return ctx;
}


/** Canal privado provider→host. Fora daqui ninguém precisa saber que existe. */
export function useFocusSessionHostChannel(): FocusSessionHostValue | null {
  return useContext(FocusSessionHostContext);
}
